/**
 * "Not yet": a promise that settles when a part of the page that is already on screen (as pre-rendered
 * HTML) is about to be *used*, so its JavaScript can be fetched and hydrated then instead of during the first
 * seconds of the load.
 *
 * Use it in the factory of a `lazy()` component that sits in a `<Suspense>` boundary of a pre-rendered page:
 *
 *   const Sections = lazy(() => whenNeeded().then(() => import('./Sections')));
 *
 * While the promise is pending React leaves the server's markup for the boundary exactly as it is (it is
 * "dehydrated": visible, styled, links work as plain links) and hydrates it, time-sliced and at low priority,
 * once the promise has settled and the chunk has arrived. On the server (the pre-render) and in tests the
 * promise is settled already, so nothing changes there.
 *
 * "Needed" means the first of
 *   · the person does something — scrolls, touches, presses a key or a pointer button, turns the wheel;
 *   · the address names a section (`#shop`): the jump has to land on working content;
 *   · the browser has been idle for `idleAfterMs` after the gate was created (the content is then fetched
 *     in the background, so it is there before it is wanted).
 * Nothing here looks at the viewport on purpose: the first scroll event comes before any section does, and
 * an observer per page would be one more thing to hydrate.
 */

export interface GateOptions {
  /**
   * Selector of an element in the part's server markup. The gate only holds while the document already
   * contains it — that is, while the part is being *hydrated*. A part that is rendered for the first time on
   * the client (a navigation from another page) has nothing on screen to protect and loads at once.
   */
  markup?: string;
  /** How long the page may sit untouched before the content is fetched anyway. Default 4000 ms. */
  idleAfterMs?: number;
  /** Treat a hash in the address (`#shop`) as "needed". Default true. */
  hash?: boolean;
}

export const DEFAULT_IDLE_AFTER_MS = 4000;

/** Events that mean a person is using the page. Passive and captured: they must never get in anyone's way. */
const INPUT_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'] as const;

export function whenNeeded({
  markup,
  idleAfterMs = DEFAULT_IDLE_AFTER_MS,
  hash = true,
}: GateOptions = {}): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (markup !== undefined && document.querySelector(markup) === null) return Promise.resolve();
  if (hash && window.location.hash.length > 1) return Promise.resolve();

  return new Promise<void>((resolve) => {
    const cleanups: (() => void)[] = [];
    const done = (): void => {
      for (const cleanup of cleanups) cleanup();
      resolve();
    };

    for (const type of INPUT_EVENTS) {
      window.addEventListener(type, done, { capture: true, passive: true, once: true });
      cleanups.push(() => {
        window.removeEventListener(type, done, { capture: true });
      });
    }

    const timer = window.setTimeout(() => {
      // fetch it in a gap, not in the middle of something
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(done, { timeout: 1500 });
      } else done();
    }, idleAfterMs);
    cleanups.push(() => {
      window.clearTimeout(timer);
    });
  });
}

/**
 * Resolve once the page has been quiet for `delayMs` *and* the browser is idle (at most `timeoutMs` later).
 * For enhancements that must not compete with the load — the hero's WebGL scene: until it arrives the poster, which
 * is the scene's first frame, stands in for it. Settles at once where there is no browser to wait for.
 */
export function afterQuiet(delayMs: number, timeoutMs = 2000): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(
          () => {
            resolve();
          },
          { timeout: timeoutMs },
        );
      } else resolve();
    }, delayMs);
  });
}
